import { observer } from 'mobx-react-lite'
import cx from 'classnames'
import dayjs from 'dayjs'
import { Workspace } from 'types'
import { DeactivateWorkspace } from '../Workspace/DeactivateWorkspace/DeactivateWorkspace'
import { DeactivateUser } from '../Account/DeactivateUser/DeactivateUser'
import SettingsStyles from '../SettingsModal.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useEffect, useState } from 'react'
import client from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import useIsMounted from 'ismounted'
import { WorkspaceSettings as WorkspaceSettingsType } from 'types'
import WorkspaceStyles from '../Workspace/WorkspaceSettings.module.scss'
import Styles from './DangerZone.module.scss'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { useRootStore } from 'stores/RootStore'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { AutopilotSettings } from '../Workspace/AutopilotSettings/AutopilotSettings'
import { useOpenSettings } from 'hooks/useOpenSettings'
import { SettingScreen } from '../types'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { AutopilotPlansModal } from 'components/Settings/Workspace/AutopilotPlans/AutopilotPlansModal'
import { useUpdateAutopilotSubscription } from 'hooks/useUpdateAutopilotSubscription'
import { useVirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useVirtualMarketerPlan'

interface Props {
  workspace: Workspace
}

export const DangerZone = observer(({ workspace }: Props) => {
  const tierUsageStore = useRootStore().tierUsageStore
  const { currentSubscription, autopilotPlans } = tierUsageStore
  const { data: brandPlan } = useCurrentBrandPlan()
  const { data: virtualMarketerPlan } = useVirtualMarketerPlan()
  const { updateSubscription, isUpdating } = useUpdateAutopilotSubscription({
    skipRedirect: true,
  })
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const { isMobile } = useResponsiveDesign()
  const { closeModal, openModal } = useModals()
  const { openSettings } = useOpenSettings()
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettingsType>()
  const isMounted = useIsMounted()

  const starterPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)
  const growthPlan = autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotGrowth)
  const isCanceled = !!currentSubscription?.cancel_at || currentSubscription?.status === 'canceled'
  const isTrialing = currentSubscription?.status === 'trialing'
  const isFullyCanceled = currentSubscription?.status === 'canceled'
  const autopilotEndDate = workspace?.pause_autopilot_publish_at
  const autoPilotEndDateIsPast = autopilotEndDate && dayjs(autopilotEndDate).isBefore(dayjs())

  useEffect(() => {
    if (!workspace?.key) return

    const loadWorkspaceSettings = async (workspaceKey: string) => {
      const res = await client.getWorkspaceSettings(workspaceKey)
      if (res.type === SUCCESS_RESPONSE_TYPE && isMounted.current) {
        setWorkspaceSettings(res.data.company)
      }
    }

    loadWorkspaceSettings(workspace.key)
  }, [workspace?.key, isMounted])

  return (
    <>
      {!isMobile && !isSettingsModalUpdateEnabled && (
        <header className={`${SettingsStyles.subHeader} ${Styles.header}`}>
          <Heading level={3}>Danger Zone</Heading>
        </header>
      )}
      {!isMobile && isSettingsModalUpdateEnabled && (
        <Modal.Header title="Danger Zone" className={SettingsStyles.newHeader} />
      )}

      {brandPlan?.id && (
        <>
          <div className={Styles.section}>
            <AutopilotSettings brandPlanId={brandPlan.id} />
          </div>
          <div className={WorkspaceStyles.divider} />
        </>
      )}

      {/* If the subscription is fully canceled, we don't show the reactivate/cancel subscription button */}
      {!isTrialing && !isFullyCanceled && (
        <>
          <div className={Styles.section}>
            <div className={Styles.sectionHeader}>
              <Heading level={3}>{isCanceled ? 'Reactivate Subscription' : 'Cancel Subscription'}</Heading>
              <div className={Styles.buttonContainer}>
                {isCanceled ? (
                  <Button
                    size="md"
                    variant="primary"
                    data-testid="reactivate-subscription"
                    isDisabled={isUpdating}
                    onPress={() => {
                      let plan = null

                      switch (currentSubscription?.plan) {
                        case PaymentPlanKey.BlazeAutopilotGrowth:
                          plan = growthPlan
                          break
                        case PaymentPlanKey.BlazeAutopilotStarter:
                          plan = starterPlan
                          break
                        case PaymentPlanKey.VirtualMarketer:
                          plan = virtualMarketerPlan
                          break
                      }

                      if (!plan) {
                        openModal(
                          AutopilotPlansModal,
                          {
                            skipRedirect: true,
                            skipCloseAllModals: true,
                          },
                          { newStack: true },
                        )
                        return
                      }

                      if (plan.key === PaymentPlanKey.VirtualMarketer) {
                        updateSubscription({
                          paymentMethodId: undefined,
                          priceId: currentSubscription?.prices?.[0]?.id,
                          reactivate: true,
                        })
                        return
                      }

                      openModal(
                        AutopilotPlansModal,
                        {
                          initialStep: 'selected',
                          initialPlanAndPrice: {
                            plan: plan,
                            price: currentSubscription?.prices?.[0],
                          },
                          skipRedirect: true,
                          skipCloseAllModals: true,
                          reactivate: true,
                        },
                        { newStack: true },
                      )
                    }}
                  >
                    Reactivate Subscription
                  </Button>
                ) : (
                  <Button
                    size="md"
                    variant="secondary"
                    onPress={() => {
                      openModal(UpgradePlanModal, {
                        initialPage: 'provideReason',
                        onClose: () => {
                          closeModal()
                          tierUsageStore.refetch(workspace.id)
                        },
                        showSuccessModal: false,
                      })
                    }}
                    data-testid="cancel-subscription"
                  >
                    Cancel Subscription
                  </Button>
                )}
              </div>
            </div>
            <div className={Styles.sectionContent}>
              <div className={Styles.warningText}>
                <p>
                  {isCanceled
                    ? `Your subscription has been cancelled${
                        autopilotEndDate
                          ? ` and your autopublishing ${autoPilotEndDateIsPast ? 'ran until' : 'runs until'} ${dayjs(
                              autopilotEndDate,
                            ).format('MMM D, YYYY')}`
                          : '.'
                      }`
                    : 'Stop future billing. Access continues until the end of the billing period.'}
                </p>
              </div>
            </div>
          </div>
          <div className={WorkspaceStyles.divider} />
        </>
      )}
      <div className={Styles.section}>
        <div className={Styles.sectionHeader}>
          <Heading level={3}>Delete Workspace</Heading>
          <div className={Styles.buttonContainer}>
            <DeactivateWorkspace workspace={workspaceSettings} workspaceName={workspace.name} />
          </div>
        </div>
        <div className={Styles.sectionContent}>
          <div className={Styles.warningText}>
            <p>
              Permanently deactivate this workspace. <strong>This can't be undone.</strong>
            </p>
            <p className={cx(Styles.warningText, Styles.muted)}>
              Your subscription will be canceled. Posts, integrations, and member access will be removed.
            </p>
          </div>
        </div>
      </div>

      <div className={WorkspaceStyles.divider} />

      <div className={Styles.section}>
        <div className={Styles.sectionHeader}>
          <Heading level={3}>Delete Account</Heading>
          <div className={Styles.buttonContainer}>
            <DeactivateUser />
          </div>
        </div>
        <div className={Styles.sectionContent}>
          <div className={Styles.warningText}>
            <p>
              Permanently delete your account and all data. <strong>This can't be undone.</strong>
            </p>
            <p className={cx(Styles.warningText, Styles.muted)}>
              You'll be signed out immediately, removed from all workspaces, and any workspace you solely manage will be
              deactivated. If you're the only admin of a workspace with other members, you'll need to{' '}
              <button type="button" className={Styles.linkButton} onClick={() => openSettings(SettingScreen.Members)}>
                assign another admin
              </button>{' '}
              first.
            </p>
          </div>
        </div>
      </div>
    </>
  )
})
